/**
 * The two ways to ask the chat dock to open: at a room, and at the support desk.
 *
 * The dock is mounted once for the whole visit and is nobody's child: App
 * renders it beside every screen rather than inside one, so the screens and
 * the dock are siblings and there is no prop that can reach from one to the
 * other (App.tsx, "Mounted once, not per screen"). Until now nothing needed
 * to: the panel was opened by its own bubble and every room in it was picked
 * inside it.
 *
 * The profile screen's team list is the first thing outside the dock with a
 * room worth opening. A team's room is the only place its roster can actually
 * talk, so a list of teams that cannot open one is a list of names.
 *
 * ## Why this is a store and not a route
 *
 * Every other screen change in this product is a URL — that is decision 5 in
 * docs/browser.md and it is right, because a level is a place. A chat room is
 * not a place in that sense: the dock is a panel over whatever screen you are
 * already on, it survives navigation, and where it is docked and whether it is
 * open are preferences rather than history. Putting a room in the query string
 * would make "open the team room" a navigation that changes the screen behind
 * the panel, and would put a back button in the middle of a conversation.
 *
 * ## The shape
 *
 * A request, not a state. Nothing is remembered: this carries "somebody asked
 * for this room, now" to whoever is listening, and a listener that is not
 * mounted means nothing happened. There is exactly one listener in practice —
 * the dock — and it is a `Set` anyway, because the alternative is a module
 * where the second subscriber silently replaces the first.
 */
import type { RoomId } from '@engineer-os/chat';

const listeners = new Set<(room: RoomId) => void>();

/**
 * Ask the dock to open, showing this room.
 *
 * The room id comes off the wire — `Team.room`, which the Worker built with
 * the protocol's own function — and is never assembled by a caller
 * (lib/teams.ts, "Why the room id comes from the server").
 */
export function openRoom(room: RoomId): void {
  for (const listener of listeners) listener(room);
}

/** Listen for those asks. Returns the unsubscribe, the way the stores do. */
export function onOpenRoom(listener: (room: RoomId) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/* ── The other ask: the support desk ─────────────────────────────────────── */

/**
 * The second thing outside the dock with something in it worth opening, and
 * the one place in the product that asks for money: the offer block's line for
 * somebody the card rail will not serve (components/home/Offer.tsx).
 *
 * It is a room like the one above — a support thread *is* a room
 * (docs/support.md) — but the caller cannot name it. Which room it is depends
 * on whether this account has a thread already, and that is a question only
 * the panel's own `useSupport` has asked. So the ask carries no id: it means
 * *show me the desk*, and what the desk then shows — a conversation going, a
 * form to start one, or a sign-in prompt — is the panel's to decide.
 *
 * ## Why this one is remembered and the room ask is not
 *
 * `openRoom` is fire-and-forget because its only caller is a screen inside the
 * app, where the dock is mounted beside it and has been since the visit began.
 * This one is pressed on the landing page, where there is no dock at all: the
 * page mounts one *in response* to the ask (components/home/Landing.tsx), and
 * a dock that arrives a render later would otherwise have missed the thing it
 * was mounted for.
 *
 * So the ask waits to be claimed. `takeSupportAsk` is how a dock claims it —
 * on mount, and again on every ask afterwards — and it clears as it reads, so
 * one press opens the panel exactly once and a second dock mounting tomorrow
 * does not reopen it.
 */
const supportListeners = new Set<() => void>();
let supportWanted = false;

/** Ask the dock to open on the support desk, mounting one if that is what it takes. */
export function openSupport(): void {
  supportWanted = true;
  for (const listener of supportListeners) listener();
}

/** Listen for those asks. */
export function onOpenSupport(listener: () => void): () => void {
  supportListeners.add(listener);
  return () => {
    supportListeners.delete(listener);
  };
}

/** Claim an outstanding ask, clearing it. True at most once per press. */
export function takeSupportAsk(): boolean {
  const wanted = supportWanted;
  supportWanted = false;
  return wanted;
}
